import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, DollarSign, TrendingUp, Clock, MapPin, Calendar,
  Car, Users, Star, ChevronDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DriverAnalytics() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [rides, setRides] = useState([]);
  const [timeRange, setTimeRange] = useState('all'); // all, week, month, year
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);

  useEffect(() => {
    if (user.role !== 'driver') {
      navigate('/rider');
      return;
    }
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const [statsRes, historyRes] = await Promise.all([
          axios.get(`${API}/drivers/analytics?time_range=${timeRange}`),
          axios.get(`${API}/rides/history`)
        ]);
        setStats(statsRes.data);
        setRides(historyRes.data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
        // Calculate stats from ride history as fallback
        try {
          const historyRes = await axios.get(`${API}/rides/history`);
          const rideData = historyRes.data;
          setRides(rideData);
          
          // Calculate stats locally
          const completedRides = rideData.filter(r => r.status === 'arrived' || r.status === 'completed');
          const cancelledRides = rideData.filter(r => r.status === 'cancelled');
          
          // Filter by time range
          const now = new Date();
          const filteredRides = completedRides.filter(r => {
            const rideDate = new Date(r.created_at);
            if (timeRange === 'week') {
              const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
              return rideDate >= weekAgo;
            } else if (timeRange === 'month') {
              const monthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
              return rideDate >= monthAgo;
            } else if (timeRange === 'year') {
              const yearAgo = new Date(now - 365 * 24 * 60 * 60 * 1000);
              return rideDate >= yearAgo;
            }
            return true; // all time
          });
          
          const totalEarnings = filteredRides.reduce((sum, r) => sum + (r.fare || 0), 0);
          const totalDistance = filteredRides.reduce((sum, r) => sum + (r.distance || 0), 0);
          const totalTime = filteredRides.reduce((sum, r) => sum + (r.eta || 0), 0);
          
          // Group by day for chart
          const dailyEarnings = {};
          filteredRides.forEach(r => {
            const day = new Date(r.created_at).toLocaleDateString();
            if (!dailyEarnings[day]) {
              dailyEarnings[day] = { earnings: 0, rides: 0 };
            }
            dailyEarnings[day].earnings += r.fare || 0;
            dailyEarnings[day].rides += 1;
          });
          
          setStats({
            total_earnings: totalEarnings,
            total_rides: filteredRides.length,
            total_distance: totalDistance,
            total_time: totalTime,
            average_fare: filteredRides.length > 0 ? totalEarnings / filteredRides.length : 0,
            average_distance: filteredRides.length > 0 ? totalDistance / filteredRides.length : 0,
            cancelled_rides: cancelledRides.length,
            completion_rate: completedRides.length + cancelledRides.length > 0 
              ? (completedRides.length / (completedRides.length + cancelledRides.length)) * 100 
              : 100,
            daily_earnings: Object.entries(dailyEarnings)
              .map(([date, data]) => ({ date, ...data }))
              .sort((a, b) => new Date(a.date) - new Date(b.date))
              .slice(-14) // Last 14 days
          });
        } catch (fallbackError) {
          // Silent fallback
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user.role, navigate, timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [statsRes, historyRes] = await Promise.all([
        axios.get(`${API}/drivers/analytics?time_range=${timeRange}`),
        axios.get(`${API}/rides/history`)
      ]);
      setStats(statsRes.data);
      setRides(historyRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      // Calculate stats from ride history as fallback
      try {
        const historyRes = await axios.get(`${API}/rides/history`);
        const rideData = historyRes.data;
        setRides(rideData);
        
        // Calculate stats locally
        const completedRides = rideData.filter(r => r.status === 'arrived' || r.status === 'completed');
        const cancelledRides = rideData.filter(r => r.status === 'cancelled');
        
        // Filter by time range
        const now = new Date();
        const filteredRides = completedRides.filter(r => {
          const rideDate = new Date(r.created_at);
          if (timeRange === 'week') {
            const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
            return rideDate >= weekAgo;
          } else if (timeRange === 'month') {
            const monthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
            return rideDate >= monthAgo;
          } else if (timeRange === 'year') {
            const yearAgo = new Date(now - 365 * 24 * 60 * 60 * 1000);
            return rideDate >= yearAgo;
          }
          return true; // all time
        });
        
        const totalEarnings = filteredRides.reduce((sum, r) => sum + (r.fare || 0), 0);
        const totalDistance = filteredRides.reduce((sum, r) => sum + (r.distance || 0), 0);
        const totalTime = filteredRides.reduce((sum, r) => sum + (r.eta || 0), 0);
        
        // Group by day for chart
        const dailyEarnings = {};
        filteredRides.forEach(r => {
          const day = new Date(r.created_at).toLocaleDateString();
          if (!dailyEarnings[day]) {
            dailyEarnings[day] = { earnings: 0, rides: 0 };
          }
          dailyEarnings[day].earnings += r.fare || 0;
          dailyEarnings[day].rides += 1;
        });
        
        setStats({
          total_earnings: totalEarnings,
          total_rides: filteredRides.length,
          total_distance: totalDistance,
          total_time: totalTime,
          average_fare: filteredRides.length > 0 ? totalEarnings / filteredRides.length : 0,
          average_distance: filteredRides.length > 0 ? totalDistance / filteredRides.length : 0,
          cancelled_rides: cancelledRides.length,
          completion_rate: completedRides.length + cancelledRides.length > 0 
            ? (completedRides.length / (completedRides.length + cancelledRides.length)) * 100 
            : 100,
          daily_earnings: Object.entries(dailyEarnings)
            .map(([date, data]) => ({ date, ...data }))
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .slice(-14) // Last 14 days
        });
      } catch (fallbackError) {
        toast.error('Failed to load analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  const timeRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'year', label: 'This Year' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      requested: 'text-yellow-400',
      accepted: 'text-blue-400',
      en_route: 'text-purple-400',
      arrived: 'text-green-400',
      in_progress: 'text-neon-cyan',
      completed: 'text-green-500',
      cancelled: 'text-red-400',
    };
    return colors[status] || 'text-white';
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Calculate max earnings for chart scaling
  const maxDailyEarnings = stats?.daily_earnings?.length > 0 
    ? Math.max(...stats.daily_earnings.map(d => d.earnings)) 
    : 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg text-white flex items-center justify-center">
        <div className="text-white/60">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-dark-surface">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-outfit font-bold text-2xl">Driver Analytics</h1>
                <p className="text-sm text-white/60">Track your earnings and performance</p>
              </div>
            </div>
            
            {/* Time Range Selector */}
            <div className="relative">
              <button
                onClick={() => setShowTimeDropdown(!showTimeDropdown)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                data-testid="time-range-btn"
              >
                <Calendar className="w-4 h-4" />
                <span>{timeRangeOptions.find(o => o.value === timeRange)?.label}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showTimeDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showTimeDropdown && (
                <div className="absolute right-0 mt-2 w-40 bg-dark-surface border border-white/10 rounded-xl overflow-hidden shadow-xl z-50">
                  {timeRangeOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setTimeRange(option.value);
                        setShowTimeDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-white/5 transition-colors ${
                        timeRange === option.value ? 'bg-neon-cyan/10 text-neon-cyan' : 'text-white'
                      }`}
                      data-testid={`time-range-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {/* Total Earnings */}
          <div className="bg-dark-surface rounded-2xl p-6 border border-white/5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <span className="text-sm text-white/60">Total Earnings</span>
            </div>
            <div className="text-3xl font-bold text-green-400" data-testid="total-earnings">
              ${stats?.total_earnings?.toFixed(2) || '0.00'}
            </div>
          </div>

          {/* Total Rides */}
          <div className="bg-dark-surface rounded-2xl p-6 border border-white/5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-neon-cyan/20 flex items-center justify-center">
                <Car className="w-5 h-5 text-neon-cyan" />
              </div>
              <span className="text-sm text-white/60">Completed Rides</span>
            </div>
            <div className="text-3xl font-bold text-neon-cyan" data-testid="total-rides">
              {stats?.total_rides || 0}
            </div>
          </div>

          {/* Total Distance */}
          <div className="bg-dark-surface rounded-2xl p-6 border border-white/5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-neon-purple/20 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-neon-purple" />
              </div>
              <span className="text-sm text-white/60">Miles Driven</span>
            </div>
            <div className="text-3xl font-bold text-neon-purple" data-testid="total-distance">
              {stats?.total_distance?.toFixed(1) || '0.0'}
            </div>
          </div>

          {/* Completion Rate */}
          <div className="bg-dark-surface rounded-2xl p-6 border border-white/5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                <Star className="w-5 h-5 text-yellow-400" />
              </div>
              <span className="text-sm text-white/60">Completion Rate</span>
            </div>
            <div className="text-3xl font-bold text-yellow-400" data-testid="completion-rate">
              {stats?.completion_rate?.toFixed(0) || 100}%
            </div>
          </div>
        </div>

        {/* Earnings Chart */}
        <div className="bg-dark-surface rounded-2xl p-6 border border-white/5 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-outfit font-bold text-xl">Earnings Overview</h2>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-neon-cyan"></div>
                <span className="text-white/60">Earnings</span>
              </div>
            </div>
          </div>

          {stats?.daily_earnings?.length > 0 ? (
            <div className="relative h-64">
              {/* Y-axis labels */}
              <div className="absolute left-0 top-0 bottom-8 w-12 flex flex-col justify-between text-xs text-white/40">
                <span>${maxDailyEarnings.toFixed(0)}</span>
                <span>${(maxDailyEarnings / 2).toFixed(0)}</span>
                <span>$0</span>
              </div>
              
              {/* Chart area */}
              <div className="ml-14 h-56 flex items-end justify-between gap-2">
                {stats.daily_earnings.map((day, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center justify-end">
                    {/* Bar */}
                    <div 
                      className="w-full bg-neon-cyan/80 rounded-t hover:bg-neon-cyan transition-colors cursor-pointer group relative"
                      style={{ 
                        height: `${Math.max((day.earnings / maxDailyEarnings) * 100, 2)}%`,
                        minHeight: '4px'
                      }}
                      data-testid={`chart-bar-${idx}`}
                    >
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        <div className="bg-dark-bg border border-white/20 rounded-lg px-3 py-2 text-xs whitespace-nowrap">
                          <div className="font-bold text-neon-cyan">${day.earnings.toFixed(2)}</div>
                          <div className="text-white/60">{day.rides} ride{day.rides !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                    </div>
                    {/* X-axis label */}
                    <div className="mt-2 text-xs text-white/40 truncate max-w-full">
                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-white/40">
              <div className="text-center">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No earnings data available</p>
                <p className="text-sm mt-1">Complete some rides to see your chart</p>
              </div>
            </div>
          )}
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-dark-surface rounded-xl p-5 border border-white/5">
            <div className="text-sm text-white/60 mb-1">Average Fare</div>
            <div className="text-2xl font-bold text-white">
              ${stats?.average_fare?.toFixed(2) || '0.00'}
            </div>
            <div className="text-xs text-white/40 mt-1">per ride</div>
          </div>
          <div className="bg-dark-surface rounded-xl p-5 border border-white/5">
            <div className="text-sm text-white/60 mb-1">Average Distance</div>
            <div className="text-2xl font-bold text-white">
              {stats?.average_distance?.toFixed(1) || '0.0'} miles
            </div>
            <div className="text-xs text-white/40 mt-1">per ride</div>
          </div>
          <div className="bg-dark-surface rounded-xl p-5 border border-white/5">
            <div className="text-sm text-white/60 mb-1">Total Drive Time</div>
            <div className="text-2xl font-bold text-white">
              {stats?.total_time ? (stats.total_time / 60).toFixed(1) : '0'} hrs
            </div>
            <div className="text-xs text-white/40 mt-1">estimated</div>
          </div>
        </div>

        {/* Recent Rides */}
        <div className="bg-dark-surface rounded-2xl p-6 border border-white/5">
          <h2 className="font-outfit font-bold text-xl mb-4">Recent Rides</h2>
          
          {rides.length === 0 ? (
            <div className="text-center py-10 text-white/40">
              <Car className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No rides yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rides.slice(0, 10).map((ride, idx) => (
                <div 
                  key={ride.id} 
                  className="flex items-center justify-between p-4 bg-dark-bg rounded-xl"
                  data-testid={`recent-ride-${idx}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-sm font-medium ${getStatusColor(ride.status)}`}>
                        {formatStatus(ride.status)}
                      </span>
                      <span className="text-xs text-white/40">
                        {new Date(ride.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-white/60">
                      <MapPin className="w-3 h-3 text-neon-cyan" />
                      <span className="truncate">{ride.pickup_address?.substring(0, 30)}...</span>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-lg font-bold text-neon-cyan">${ride.fare}</div>
                    <div className="text-xs text-white/40">{ride.distance?.toFixed(1)} mi</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
